import React, { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { editMspFormSchema, EditMSPFormValues } from "./schema";
import { editFormFields } from "./config";
import { FieldRenderer } from "../../../components/form/FieldRenderer";
import { EditMspFormProps } from "./types";
import { updateMsp } from "../services/mspService";
import {
  useCountryList,
  useStateList,
  useCityList,
} from "../../../hooks/useLocationData";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setError,
  setNotification,
  clearError,
  clearNotification,
} from "../../msp/slices/mspSlice";
import { EditWhiteIcon } from "../../common/icons";

const EditMSPInformationForm: React.FC<EditMspFormProps> = ({
  msp,
  onSuccess,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { notification } = useAppSelector((state) => state.msp);
  const [formInitialized, setFormInitialized] = React.useState(false);
  const hasInitialized = useRef(false);
  const isInitializing = useRef(false);
  const [submitting, setSubmitting] = React.useState(false);

  // Location hooks
  const {
    countryList: countries,
    isCountryLoading,
    countryLoadingError,
  } = useCountryList();

  const [selectedCountryId, setSelectedCountryId] = React.useState<number>();
  const [selectedStateId, setSelectedStateId] = React.useState<number>();

  const {
    stateList: states,
    isStateLoading,
    stateLoadingError,
  } = useStateList(selectedCountryId);

  const {
    cityList: cities,
    isCityLoading,
    cityLoadingError,
  } = useCityList(selectedStateId);

  const primaryAddress = useMemo(() => {
    return msp?.mspContacts?.[0]?.contact?.addresses?.[0] ?? null;
  }, [msp]);

  const findIdByName = (items: any[], name?: string): string => {
    if (!name || items.length === 0) return "";
    const item = items.find(
      (it: any) => it.name?.toLowerCase().trim() === name?.toLowerCase().trim()
    );
    return item?.id?.toString() ?? "";
  };

  // Default values
  const defaultValues = useMemo((): EditMSPFormValues => {
    if (!msp) {
      return {
        organization_name: "",
        website: "",
        company_phone: "",
        company_email: "",
        tax_id: "",
        company_street_address: "",
        company_city_id: "",
        company_state_id: "",
        company_country_id: "",
        company_postal_code: "",
      };
    }

    return {
      organization_name: msp.msp_name || "",
      website: msp.website || "",
      company_phone: msp.mobile_phone || "",
      company_email: msp.corporate_email || "",
      tax_id: msp.tax_id || "",
      company_street_address: primaryAddress?.address || "",
      company_postal_code: primaryAddress?.postal_code || "",
      company_country_id: "",
      company_state_id: "",
      company_city_id: "",
    };
  }, [msp, primaryAddress]);

  const methods = useForm<EditMSPFormValues>({
    resolver: zodResolver(editMspFormSchema) as any,
    defaultValues,
    mode: "onChange",
  });
  const { reset } = methods;
  const { handleSubmit, watch, setValue, formState } = methods;

  const watchCountry = watch("company_country_id");
  const watchState = watch("company_state_id");

  useEffect(() => {
    const combinedError =
      countryLoadingError || stateLoadingError || cityLoadingError || null;

    if (combinedError) dispatch(setError(combinedError));
    else dispatch(clearError());
  }, [countryLoadingError, stateLoadingError, cityLoadingError, dispatch]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => dispatch(clearNotification()), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification, dispatch]);

  // Initialize country
  useEffect(() => {
    if (
      !hasInitialized.current &&
      countries.length > 0 &&
      primaryAddress?.country?.name &&
      !formInitialized
    ) {
      const countryId = findIdByName(countries, primaryAddress.country.name);
      if (countryId) {
        setValue("company_country_id", countryId, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setSelectedCountryId(Number.parseInt(countryId));
      }
      hasInitialized.current = true;
    }
  }, [countries, primaryAddress, setValue, formInitialized]);

  // When country changes, fetch states
  useEffect(() => {
    if (watchCountry && watchCountry !== "") {
      setSelectedCountryId(Number.parseInt(watchCountry));
    }
  }, [watchCountry]);

  // Prefill state after states load
  useEffect(() => {
    if (
      states.length > 0 &&
      primaryAddress?.state?.name &&
      watchCountry &&
      !formInitialized
    ) {
      const stateId = findIdByName(states, primaryAddress.state.name);
      if (stateId) {
        isInitializing.current = true;
        setValue("company_state_id", stateId, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setSelectedStateId(Number.parseInt(stateId));
      }
    }
  }, [states, primaryAddress, watchCountry, setValue, formInitialized]);

  // When state changes, fetch cities
  useEffect(() => {
    if (watchState && watchState !== "") {
      setSelectedStateId(Number.parseInt(watchState));
    }
  }, [watchState]);

  // Prefill city after cities load
  useEffect(() => {
    if (
      cities.length > 0 &&
      primaryAddress?.city?.name &&
      watchState &&
      !formInitialized
    ) {
      const cityId = findIdByName(cities, primaryAddress.city.name);
      if (cityId) {
        setValue("company_city_id", cityId, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setTimeout(() => {
          setFormInitialized(true);
          isInitializing.current = false;
        }, 100);
      }
    }
  }, [cities, primaryAddress, watchState, setValue, formInitialized]);

  // Build select options
  const updatedFields = useMemo(() => {
    return editFormFields.map((field) => {
      if (field.name === "website") {
        return {
          ...field,
          disabled: true,
        };
      }
      if (field.name === "company_country_id") {
        return {
          ...field,
          options: [
            { label: "-- Select Country --", value: "" },
            ...countries.map((country: any) => ({
              label: country.name,
              value: country.id.toString(),
            })),
          ],
          loading: isCountryLoading,
        };
      }

      if (field.name === "company_state_id") {
        return {
          ...field,
          options: [
            { label: "-- Select State --", value: "" },
            ...states.map((state: any) => ({
              label: state.name,
              value: state.id.toString(),
            })),
          ],
          loading: isStateLoading,
          disabled: !watchCountry || isStateLoading,
        };
      }

      if (field.name === "company_city_id") {
        return {
          ...field,
          options: [
            { label: "-- Select City --", value: "" },
            ...cities.map((city: any) => ({
              label: city.name,
              value: city.id.toString(),
            })),
          ],
          loading: isCityLoading,
          disabled: !watchState || isCityLoading,
        };
      }

      return field;
    });
  }, [
    countries,
    states,
    cities,
    isCountryLoading,
    isStateLoading,
    isCityLoading,
    watchCountry,
    watchState,
  ]);

  useEffect(() => {
    hasInitialized.current = false;
    isInitializing.current = false;
    setFormInitialized(false);

    reset(defaultValues);

    setSelectedCountryId(undefined);
    setSelectedStateId(undefined);
  }, [msp?.id, defaultValues, reset]);

  const onSubmit = async (data: EditMSPFormValues) => {
    if (!formInitialized || isInitializing.current) {
      dispatch(
        setNotification({
          message: "Please wait while we load location data...",
          type: "error",
        })
      );
      return;
    }

    try {
      setSubmitting(true);
      dispatch(clearError());

      const apiPayload = {
        company_name: data.organization_name.trim(),
        website: data.website || "",
        contact_no: data.company_phone || "",
        email_id: data.company_email.trim().toLowerCase() || "",
        street: data.company_street_address || "",
        country: data.company_country_id
          ? Number.parseInt(data.company_country_id)
          : 0,
        state: data.company_state_id
          ? Number.parseInt(data.company_state_id)
          : 0,
        city: data.company_city_id ? Number.parseInt(data.company_city_id) : 0,
        zip_code: data.company_postal_code || "",
        tax_id: data.tax_id || "",
      };

      const response = await updateMsp(msp?.id, apiPayload);

      dispatch(
        setNotification({
          message: response?.message || "MSP information updated successfully!",
          type: "success",
        })
      );
      onSuccess?.();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update MSP information";

      dispatch(setError(errorMessage));
      dispatch(
        setNotification({
          message: errorMessage,
          type: "error",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isFormLoading = !formInitialized || isInitializing.current;

  return (
    <div className="w-full h-full bg-gray-50">
      <div className="mx-auto bg-white shadow-md rounded-md">
        {/* Form Header */}
        <div className="px-6 py-4">
          <h2 className="text-xl font-semibold text-[#4F4F4F]">
            Edit MSP Information
          </h2>
        </div>

        {/* Form Content */}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-6 py-6">
              <h3 className="text-lg font-medium text-[#000000] mb-4">
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {updatedFields.map((field) => (
                  <FieldRenderer key={field.name} cfg={field} />
                ))}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="px-6 py-4 flex justify-center gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-8 py-2.5 rounded-md text-sm font-semibold text-gray-800 bg-[#E0E0E0] hover:bg-[#D5D5D5] focus:outline-none transition"
                disabled={submitting || isFormLoading}
              >
                CANCEL
              </button>

              {/* UPDATE BUTTON */}
              <button
                type="submit"
                disabled={submitting || isFormLoading || !formState.isValid}
                className="px-8 py-2 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 text-white bg-[#1E64E4] hover:bg-[#155BCA] focus:outline-none transition disabled:opacity-60"
              >
                <span className="w-5 h-5">
                  <EditWhiteIcon />
                </span>
                {submitting ? "UPDATING..." : "UPDATE"}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default EditMSPInformationForm;
